import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  startLivestream,
  joinLivestream,
  stopLivestream,
  isJoinResponseGranted,
  isJoinResponsePaymentRequired,
  getJoinResponseLivestream,
  getJoinResponseSessionUrl,
  getJoinResponseTag,
  parseJoinLivestreamResponse
} from '../../utils/livestreamApi';
import { handleLivestreamError, LIVESTREAM_ERRORS } from '../../utils/livestreamErrorHandler';
import useLivestreamPermissions from '../../hooks/useLivestreamPermissions';

// Main StreamActionButton Component
export default function StreamActionButton({
  livestream,
  user,
  onSuccess,
  onClick,
  disabled,
  buttonVariant,
  children
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const {
    canManageLivestream,
    canStartLivestream,
    canStopLivestream,
    canJoinLivestream,
  } = useLivestreamPermissions(livestream);
  
  // Early return if user is not available
  if (!user || !user.id) {
    return null;
  }

  const handleClick = async () => {
    if (!livestream?.id) {
      handleLivestreamError(new Error(LIVESTREAM_ERRORS.NO_LIVESTREAM_ID), {
        action: 'stream action',
        showToast: true
      });
      return;
    }

    if (onClick) {
      onClick();
      return;
    }

    if (disabled || loading) {
      return;
    }

    try {
      setLoading(true);

      // Handle different livestream statuses
      if (livestream.status === 'SCHEDULED' && canStartLivestream) {
        console.log('🚀 Starting scheduled livestream');
        const response = await startLivestream(livestream.id);
        const parsedResponse = parseJoinLivestreamResponse(response);
        
        console.log('🔍 Parsed Start Response:', parsedResponse);

        if (parsedResponse.isGranted) {
          console.log('✅ Start Granted');
          const livestreamData = parsedResponse.livestream;
          const tag = parsedResponse.tag;
          console.log('📺 Start Livestream Data:', livestreamData);
          console.log('🏷️ Start Tag:', tag);

          if (livestreamData?.watchUrl) {
            console.log('🔗 Navigating to watch page (start):', livestreamData.id);
            navigate(`/livestream/watch/${livestreamData.id}`, {
              state: { livestream: livestreamData }
            });
          } else {
            console.log('🔄 Refreshing livestream data (start)');
            if (onSuccess) {
              onSuccess();
            }
          }
        } else if (parsedResponse.isPaymentRequired) {
          console.log('💳 Start Unexpectedly Requires Payment');
          const sessionUrl = parsedResponse.sessionUrl;
          if (sessionUrl) {
            console.log('🔗 Opening payment session (start):', sessionUrl);
            window.open(sessionUrl, '_blank', 'noopener');
            toast.info(`Payment required: $${livestream.viewersPrice || 'TBD'}. Please complete payment to start the stream.`);
          } else {
            console.error('❌ Payment required but no session URL provided by backend');
            toast.error('Payment required but no payment URL provided. Please contact support.');
          }
        } else {
          console.error('❌ Unexpected start response:', parsedResponse);
          handleLivestreamError(new Error('Unexpected response from server'), {
            action: 'start livestream',
            showToast: true
          });
        }
      } else if (livestream.status === 'IN_PROGRESS' && canJoinLivestream) {
        console.log('🎯 Joining in-progress livestream');
        const response = await joinLivestream(livestream.id);
        const parsedResponse = parseJoinLivestreamResponse(response);
        
        console.log('🔍 Parsed Join Response:', parsedResponse);
        
        if (parsedResponse.isPaymentRequired) {
          console.log('💳 Payment Required Response - Opening payment session');
          const sessionUrl = parsedResponse.sessionUrl;
          if (sessionUrl) {
            // Open payment session in new tab
            console.log('🔗 Opening payment session:', sessionUrl);
            window.open(sessionUrl, '_blank', 'noopener');
            toast.info(`Payment required: $${livestream.viewersPrice || 'TBD'}. Please complete payment to watch the stream.`);
          } else {
            console.error('❌ Payment required but no session URL provided by backend');
            toast.error('Payment required but no payment URL provided. Please contact support.');
          }
        } else if (parsedResponse.isGranted) {
          console.log('✅ Access Granted - User has access to livestream');
          // User actually has access (backend knows better than frontend)
          const livestreamData = parsedResponse.livestream;
          const tag = parsedResponse.tag;
          console.log('📺 Join Livestream Data:', livestreamData);
          console.log('🏷️ Join Tag:', tag);

          if (livestreamData?.watchUrl) {
            // Direct access with watch URL
            console.log('🔗 Navigating to watch page (join):', livestreamData.id);
            navigate(`/livestream/watch/${livestreamData.id}`, {
              state: { livestream: livestreamData }
            });
          } else {
            // Free or already purchased - refresh to get updated data
            console.log('🔄 Refreshing livestream data (join)');
            if (onSuccess) {
              onSuccess();
            }
          }
        } else {
          console.error('❌ Unexpected join response:', parsedResponse);
          handleLivestreamError(new Error('Unexpected response from server'), {
            action: 'join livestream',
            showToast: true
          });
        }
      } else if (livestream.status === 'IN_PROGRESS' && canStopLivestream) {
        console.log('⏹️ Stopping livestream');
        await stopLivestream(livestream.id);
        toast.success('Livestream stopped successfully');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        console.log('ℹ️ No action available for current status and permissions');
        toast.info('No action available for this livestream');
      }
    } catch (error) {
      console.error('❌ StreamActionButton Error:', error);
      handleLivestreamError(error, {
        action: 'stream action',
        showToast: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine button text and variant
  const getButtonConfig = () => {
    if (livestream.status === 'SCHEDULED' && canStartLivestream) {
      return {
        text: children || 'Start Stream',
        variant: 'primary',
        icon: '▶'
      };
    } else if (livestream.status === 'IN_PROGRESS' && canJoinLivestream) {
      return {
        text: children || 'Join Stream',
        variant: 'primary',
        icon: '🎥'
      };
    } else if (livestream.status === 'IN_PROGRESS' && canStopLivestream) {
      return {
        text: children || 'Stop Stream',
        variant: 'danger',
        icon: '⏹'
      };
    } else {
      return {
        text: children || 'View Details',
        variant: 'secondary',
        icon: '👁'
      };
    }
  };

  const buttonConfig = getButtonConfig();
  const isDisabled = disabled || loading || !livestream?.id;

  // Button classes based on variant
  const getButtonClasses = () => {
    const baseClasses = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm font-primary text-sm font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed";
    
    switch (buttonConfig.variant) {
      case 'primary':
        return `${baseClasses} bg-primary text-white border border-primary hover:bg-opacity-90 hover:-translate-y-0.5 hover:shadow-small`;
      case 'danger':
        return `${baseClasses} bg-error text-white border border-error hover:bg-opacity-90`;
      case 'success':
        return `${baseClasses} bg-success text-white border border-success hover:bg-opacity-90`;
      default:
        return `${baseClasses} bg-transparent text-text-primary border border-border hover:bg-hover hover:border-primary`;
    }
  };

  return (
    <button
      className={getButtonClasses()}
      onClick={handleClick}
      disabled={isDisabled}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          Processing...
        </>
      ) : (
        <>
          <span>{buttonConfig.icon}</span>
          {buttonConfig.text}
        </>
      )}
    </button>
  );
}

StreamActionButton.propTypes = {
  livestream: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    status: PropTypes.string.isRequired,
    description: PropTypes.string,
    viewersPrice: PropTypes.number,
    requiresPayment: PropTypes.bool,
    freeLivestream: PropTypes.bool,
    liveStreamPurchaseIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    court: PropTypes.shape({
      owner: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    })
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    roles: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onSuccess: PropTypes.func,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success']),
  children: PropTypes.node
};

StreamActionButton.defaultProps = {
  onSuccess: () => {},
  onClick: null,
  disabled: false,
  buttonVariant: 'primary',
  children: null
};